// components
import { MenuItem } from '@mui/material';
import GridPageAbstract from '../abstract/GridPageAbstract';
import Iconify from '../../components/iconify';
import ConfirmDialog from '../dialogs/ConfirmDialog';
import TgSessionLoginDialog from '../dialogs/TgSessionLoginDialog';
import TgSessionSendDialog from '../dialogs/TgSessionSendDialog';
import { mergeHeadWithStorage } from '../../utils/gridHeadStorage';
import { formatApiErrors, hasError } from '../../utils/formatError';
import { TG_SESSION_STATUS } from '../../constants/tgSessionStatus';
import i18n from '../../i18n';

// Must match action-permissions.yml in x-backend and the constants in
// ActionPermissions exactly. Logging in and out is one permission — both decide
// whether this account is attached at all — and sending is another: it writes
// into chats as the account holder.
const LOGIN_ACTION = 'tg-session-login';
const SEND_ACTION = 'tg-session-send';

// Nothing to log out of: no login has finished, or it has already been ended.
const NOT_LOGGED_IN = ['new', 'logged_out'];

/**
 * Whether tg-client keeps this session connected — its own rule for which
 * sessions it fetches from core. Anything else answers SESSION_NOT_RUNNING to a
 * send, so the action is not offered.
 */
const isConnectable = (row) =>
    row?.status === 'active' && row?.isActive === true && row?.isArchived !== true;

export default class TgSessionsPage extends GridPageAbstract {
    constructor(props) {
        super(props);
        this.Name = 'publicTgSessions';
        this.Server = this.props.context.getState().server.publicTgSessions;
        this.MenuDisplayType = 'menu';
        this.searchFields = ['name', 'phone'];
        // permissions.yml denies delete for every role: a session row is the
        // binding to a Telegram account, and dropping it would orphan its chats
        // and message history. Log out and archive instead.
        this.disableGlobalDelete = true;

        const defaultHead = [
            { id: 'name', required: true, label: 'name', alignRight: false, cell: 'Text', displayGrid: true },
            // International format, as Telegram wants it: +79991234567.
            { id: 'phone', required: true, label: 'phone', alignRight: false, cell: 'Text', displayGrid: true },

            // Runtime state, written by core from what tg-client reports — the
            // cabinet's request DTO does not even carry these, so a save could not
            // change them if it tried.
            {
                id: 'status',
                label: 'status',
                alignRight: false,
                cell: 'State',
                items: TG_SESSION_STATUS,
                displayGrid: true,
                disableEditor: true
            },
            { id: 'tgUsername', label: 'tgUsername', alignRight: false, cell: 'Text', displayGrid: true, disableEditor: true },
            // Text, not Number: a Telegram id exceeds 2^53 and travels as a string,
            // and a thousands separator would make it uncopyable.
            { id: 'tgUserId', label: 'tgUserId', alignRight: false, cell: 'Text', displayGrid: true, disableEditor: true },
            // tg-client's heartbeat. A stale one means nobody is reading the chats,
            // and a tg_session payin callback refuses new payins until it is fresh again.
            { id: 'lastConnectedAt', label: 'lastConnectedAt', alignRight: false, cell: 'DateTime', displayGrid: true, disableEditor: true },
            { id: 'statusError', label: 'statusError', alignRight: false, cell: 'BigText', displayGrid: true, disableEditor: true },

            { id: 'isActive', label: 'isActive', alignRight: false, cell: 'Enable', displayGrid: true },
            { id: 'isArchived', label: 'isArchived', alignRight: false, cell: 'Boolean', displayGrid: true },
            {
                id: 'modifiedReason',
                label: 'modifiedReason',
                alignRight: false,
                cell: 'BigText',
                displayGrid: true,
                disableEditor: true
            },
            {
                id: 'modifiedBy',
                label: 'modifiedBy',
                alignRight: false,
                cell: 'Select',
                object: 'modifiedBy',
                show: 'firstName+lastName',
                displayGrid: true,
                disableEditor: true,
                customSort: 'modifiedBy.firstName'
            },
            {
                id: 'dateCreatedModified',
                elements: ['dateCreated', 'dateModified'],
                label: 'dateCreatedModified',
                alignRight: false,
                cell: 'CreateModify',
                disableEditor: true,
                displayGrid: true,
                customSort: 'dateCreated'
            }
        ];
        this.Head = mergeHeadWithStorage(this.Name, defaultHead);

        this.state = {
            ...this.state,
            head: this.Head,
            loginSession: null,
            sendSession: null,
            logoutSession: null
        };
    }

    /** The row the menu was opened on, read before closing the menu nulls its id. */
    takeMenuRow() {
        const row = this.getMenuClickItem();
        this.handleCloseMenu();
        return row;
    }

    openLogin = () => {
        this.setState({ loginSession: this.takeMenuRow() });
    };

    closeLogin = (changed) => {
        this.setState({ loginSession: null });
        if (changed) this.retrieveData();
    };

    openSend = () => {
        this.setState({ sendSession: this.takeMenuRow() });
    };

    closeSend = () => {
        this.setState({ sendSession: null });
    };

    openLogout = () => {
        this.setState({ logoutSession: this.takeMenuRow() });
    };

    closeLogout = () => {
        this.setState({ logoutSession: null });
    };

    confirmLogout = async () => {
        const session = this.state.logoutSession;
        if (!session) return;

        const result = await this.Server.logout(session.id);
        this.setState({ logoutSession: null });
        this.props.context.update({
            alertError: hasError(result)
                ? { message: formatApiErrors(result).join('\n'), type: 'snackbar', severity: 'error' }
                : { message: i18n.t('text.tgSessionLoggedOut'), type: 'snackbar', severity: 'success' }
        });
        // Even a failed logout may have moved the status (tg-client reports what
        // it managed), so the row is re-read either way.
        await this.retrieveData();
    };

    openMessages = () => {
        const row = this.takeMenuRow();
        // TgSessionMessagesPage turns this into its session filter, shown as a
        // removable chip.
        this.props.navigate('/app/tg-session-messages', {
            state: { tgSessionId: row?.id }
        });
    };

    renderCustomMenuItems(id) {
        const row = this.List.find((item) => item.id === id);
        const items = [];

        // An array, not a fragment: hasMenuItems counts custom items with
        // Array.isArray(...) && length > 0, so a bare element does not register.
        if (this.allowedAction(LOGIN_ACTION)) {
            items.push(
                <MenuItem key={`${id}-login`} onClick={this.openLogin}>
                    <Iconify icon="eva:log-in-outline" sx={{ mr: 2 }} />
                    {i18n.t('dialogs.tgSessionLogin')}
                </MenuItem>
            );
            if (row && !NOT_LOGGED_IN.includes(row.status)) {
                items.push(
                    <MenuItem key={`${id}-logout`} onClick={this.openLogout}>
                        <Iconify icon="eva:log-out-outline" sx={{ mr: 2 }} />
                        {i18n.t('dialogs.tgSessionLogout')}
                    </MenuItem>
                );
            }
        }
        if (this.allowedAction(SEND_ACTION) && isConnectable(row)) {
            items.push(
                <MenuItem key={`${id}-send`} onClick={this.openSend}>
                    <Iconify icon="eva:paper-plane-outline" sx={{ mr: 2 }} />
                    {i18n.t('dialogs.tgSessionSend')}
                </MenuItem>
            );
        }
        if (this.allowedPermission('read', 'publicTgSessionMessages')) {
            items.push(
                <MenuItem key={`${id}-messages`} onClick={this.openMessages}>
                    <Iconify icon="eva:message-square-outline" sx={{ mr: 2 }} />
                    {i18n.t('dialogs.tgSessionMessages')}
                </MenuItem>
            );
        }

        return items;
    }

    renderCustom() {
        const { loginSession, sendSession, logoutSession } = this.state;

        return (
            <>
                {loginSession && (
                    <TgSessionLoginDialog
                        openDialog
                        session={loginSession}
                        closeDialog={this.closeLogin}
                        server={this.Server}
                    />
                )}
                {sendSession && (
                    <TgSessionSendDialog
                        openDialog
                        session={sendSession}
                        closeDialog={this.closeSend}
                        server={this.Server}
                    />
                )}
                {logoutSession && (
                    // Mounted only while needed: ConfirmDialog reads its
                    // callbacks once, in its constructor.
                    <ConfirmDialog
                        dialogOpen
                        alertTitle={i18n.t('dialogs.confirmationTitle')}
                        alertText={i18n.t('dialogs.tgSessionLogoutConfirm', {
                            name: logoutSession.name
                        })}
                        onConfirm={this.confirmLogout}
                        onCancel={this.closeLogout}
                    />
                )}
            </>
        );
    }
}
